import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProduct } from '../../../utils/queries';
import Alert from '../../../components/alert';
import DetailsTab from './DetailsTab';
import OptionsTab from './OptionsTab';
import VariantsTab from './VariantsTab';
import KitItemsTab from './KitItemsTab';
import PricingTab from './PricingTab';
import ImagesTab from './ImagesTab';
import ErpTab from './ErpTab';

const PRODUCT_TABS = [
	{ id: 'details', label: 'Details' },
	{ id: 'options', label: 'Options' },
	{ id: 'variants', label: 'Variants' },
	{ id: 'pricing', label: 'Pricing' },
	{ id: 'images', label: 'Images' },
	{ id: 'erp', label: 'ERP' },
];

// Kits bundle other products rather than having their own options/variants,
// so those tabs are swapped for a Kit Items tab -- everything else (pricing,
// images, ERP estimate wiring) works identically since a kit is a normal
// products row (product_type = 'kit').
const KIT_TABS = [
	{ id: 'details', label: 'Details' },
	{ id: 'kitItems', label: 'Kit Items' },
	{ id: 'pricing', label: 'Pricing' },
	{ id: 'images', label: 'Images' },
	{ id: 'erp', label: 'ERP' },
];

export default function ProductEditor({ kitMode = false }) {
	const { id } = useParams();
	const navigate = useNavigate();
	const isNew = !id;
	const [product, setProduct] = useState(null);
	const [loading, setLoading] = useState(!isNew);
	const [tab, setTab] = useState('details');
	const isKit = product ? product.productType === 'kit' : kitMode;
	const TABS = isKit ? KIT_TABS : PRODUCT_TABS;
	const backTo = kitMode ? '/products/kits' : '/products';
	const backLabel = kitMode ? 'All Kits' : 'All Products';

	useEffect(() => {
		if (isNew) return;
		setLoading(true);
		getProduct(id).then((result) => {
			if (result?.success) setProduct(result.data);
			setLoading(false);
		});
	}, [id, isNew]);

	const handleSaved = (saved) => {
		setProduct(saved);
		if (isNew) {
			navigate(`${backTo}/${saved.productId}/edit`, { replace: true });
		}
	};

	if (loading) {
		return <div className='py-8 pr-8 text-sm text-gray-400'>Loading...</div>;
	}

	if (!isNew && !product) {
		return (
			<div className='py-8 pr-8'>
				<p className='text-sm text-gray-500'>{kitMode ? 'Kit' : 'Product'} not found.</p>
				<button type='button' className='btn-secondary mt-4' onClick={() => navigate(backTo)}>
					Back to {backLabel}
				</button>
			</div>
		);
	}

	return (
		<div className='py-8 pr-8'>
			<button
				type='button'
				className='text-sm text-gray-500 hover:text-black mb-2'
				onClick={() => navigate(backTo)}
			>
				&larr; {backLabel}
			</button>
			<h1 className='text-2xl font-light tracking-tight mb-6'>
				{product ? product.name : kitMode ? 'New Kit' : 'New Product'}
			</h1>

			{product?.paceMissingSince && (
				<div className='mb-6'>
					<Alert
						alert={{
							type: 'error',
							message: `Not found in ERP as of ${new Date(product.paceMissingSince).toLocaleString()} - this product was automatically marked discontinued.`,
						}}
					/>
				</div>
			)}

			<div className='flex gap-6 border-b border-gray-200 mb-6'>
				{TABS.map((t) => {
					const disabled = !product && t.id !== 'details';
					return (
						<button
							key={t.id}
							type='button'
							disabled={disabled}
							onClick={() => setTab(t.id)}
							className={`pb-3 text-sm ${
								disabled
									? 'text-gray-300 cursor-not-allowed'
									: tab === t.id
										? 'border-b-2 border-black text-black font-medium'
										: 'text-gray-500 hover:text-black'
							}`}
						>
							{t.label}
						</button>
					);
				})}
			</div>

			{tab === 'details' && <DetailsTab product={product} kitMode={kitMode} onSaved={handleSaved} />}
			{tab === 'options' && product && <OptionsTab product={product} />}
			{tab === 'variants' && product && <VariantsTab product={product} />}
			{tab === 'kitItems' && product && <KitItemsTab product={product} />}
			{tab === 'pricing' && product && <PricingTab product={product} onSaved={handleSaved} />}
			{tab === 'images' && product && <ImagesTab product={product} />}
			{tab === 'erp' && product && <ErpTab product={product} />}
		</div>
	);
}
